<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '../../app/store/auth.js'
import HomeHeader from './home/HomeHeader.vue'
import HeroSection from './home/HeroSection.vue'
import StatsSection from './home/StatsSection.vue'
import AboutSection from './home/AboutSection.vue'
import ServicesSection from './home/ServicesSection.vue'
import CoverageSection from './home/CoverageSection.vue'
import CtaSection from './home/CtaSection.vue'
import HomeFooter from './home/HomeFooter.vue'
import ScrollArrow from './home/ScrollArrow.vue'
import StickyCta from './home/StickyCta.vue'

const authStore = useAuthStore()
const isLoggedIn = computed(() => authStore.isAuthenticated)

const showStickyCta = ref(false)
const showScrollArrow = ref(true)
const headerHeight = 72 // Fixed header height

const checkScrollPosition = () => {
  const scrollTop = window.scrollY
  const windowHeight = window.innerHeight
  const documentHeight = document.documentElement.scrollHeight

  // Hide arrow when near bottom (within 100px)
  showScrollArrow.value = scrollTop + windowHeight < documentHeight - 100

  // Show sticky CTA banner when scrolled past hero section (only for non-logged-in users)
  const heroSection = document.querySelector('.hero-section')
  if (heroSection && !isLoggedIn.value) {
    const heroBottom = heroSection.offsetTop + heroSection.offsetHeight
    showStickyCta.value = scrollTop > heroBottom - 100
  } else {
    showStickyCta.value = false
  }
}

const scrollToStats = () => {
  const statsSection = document.querySelector('.stats-section')
  if (statsSection) {
    const targetPosition = statsSection.offsetTop - headerHeight
    window.scrollTo({ top: targetPosition, behavior: 'smooth' })
  }
}

const scrollToNextSection = () => {
  const currentScroll = window.scrollY
  const allSections = document.querySelectorAll('section, .home-footer')

  for (const section of allSections) {
    const sectionTop = section.offsetTop - headerHeight
    if (sectionTop > currentScroll + 10) {
      window.scrollTo({ top: sectionTop, behavior: 'smooth' })
      break
    }
  }
}

onMounted(() => {
  window.addEventListener('scroll', checkScrollPosition)
  checkScrollPosition()
})

onUnmounted(() => {
  window.removeEventListener('scroll', checkScrollPosition)
})
</script>

<template>
  <div class="home">
    <HomeHeader />
    <HeroSection @scroll-to-stats="scrollToStats" />
    <StatsSection />
    <AboutSection />
    <ServicesSection />
    <CoverageSection />
    <CtaSection />
    <HomeFooter />

    <ScrollArrow :visible="showScrollArrow" @scroll="scrollToNextSection" />
    <StickyCta :visible="showStickyCta" />
  </div>
</template>

<style scoped>
.home {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--bg-primary);
}

/* Prevent long-press callout on mobile */
.home button,
.home a,
.home [role='button'] {
  -webkit-touch-callout: none;
  outline: none;
}
</style>
